import { useEffect, useState } from "react";

import { ActionDialog, DialogNotice, DialogTextButton } from "@/components/common/ActionDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface SetMonthlyGoalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentGoal: number;
  onGoalUpdate: (newGoal: number) => void;
  isLoading?: boolean;
}

/** The affiliate's own monthly target, in dollars, across every brand. */
export function SetMonthlyGoalModal({
  open,
  onOpenChange,
  currentGoal,
  onGoalUpdate,
  isLoading = false,
}: SetMonthlyGoalModalProps) {
  const [goal, setGoal] = useState(currentGoal > 0 ? String(currentGoal) : "");
  const [error, setError] = useState("");

  // Reopening starts from the saved goal, not the last unsaved draft.
  useEffect(() => {
    if (open) {
      setGoal(currentGoal > 0 ? String(currentGoal) : "");
      setError("");
    }
  }, [open, currentGoal]);

  const close = () => onOpenChange(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = parseFloat(goal);
    if (Number.isNaN(value) || value < 0) {
      setError("Enter an amount of $0 or more.");
      return;
    }
    setError("");
    onGoalUpdate(value);
  };

  return (
    <ActionDialog
      open={open}
      onClose={close}
      title={currentGoal > 0 ? "Edit monthly goal" : "Set monthly goal"}
      footer={
        <>
          <DialogTextButton onClick={close} disabled={isLoading}>
            Cancel
          </DialogTextButton>
          <Button type="submit" form="monthly-goal-form" disabled={isLoading}>
            {isLoading ? "Saving…" : "Save goal"}
          </Button>
        </>
      }
    >
      <form id="monthly-goal-form" onSubmit={handleSubmit} className="space-y-3">
        <label htmlFor="monthlyGoal" className="block text-[13px] font-medium text-foreground">
          Earnings target for the month
        </label>
        <div className="relative">
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">
            $
          </span>
          <Input
            id="monthlyGoal"
            type="number"
            inputMode="decimal"
            min="0"
            step="1"
            value={goal}
            onChange={(e) => {
              setGoal(e.target.value);
              setError("");
            }}
            placeholder="500"
            className="pl-7 tabular-nums"
            disabled={isLoading}
            autoFocus
          />
        </div>
        {error && <DialogNotice tone="error">{error}</DialogNotice>}
        <p className="text-xs text-muted-foreground">Counts commission from every brand. Set it to 0 to clear it.</p>
      </form>
    </ActionDialog>
  );
}
